import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { InventoryList } from "../components/InventoryList";
import { ScopePicker, type Scope } from "../components/ScopePicker";
import { money } from "../lib/format";
import { useInventoryList } from "../lib/useInventoryList";

interface Summary {
  inventoryByStore: Array<{ store: string; units: number; value: string }>;
}

/**
 * Inventory explorer: on-hand stock broken down by the store it came from, and
 * the item list filtered to whichever store is tapped.
 */
export function Explorer() {
  const [byStore, setByStore] = useState<Summary["inventoryByStore"] | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [store, setStore] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [needle, setNeedle] = useState("");
  const [scope, setScope] = useState<Scope>("business");
  const list = useInventoryList(scope, needle, store);
  const error = summaryError ?? list.error;

  // Typing shouldn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setNeedle(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const s = await api.get<Summary>(`/items/summary?scope=${scope}`);
        if (!cancelled) setByStore(s.inventoryByStore ?? []);
      } catch (err) {
        if (!cancelled) setSummaryError(err instanceof Error ? err.message : "Could not load your inventory");
      }
    })();
    return () => { cancelled = true; };
  }, [scope]);

  const clear = () => { setStore(null); setQ(""); };
  const filtering = !!store || !!needle;
  const title = store
    ? `${store} (${list.total})`
    : needle
      ? `Matches (${list.total})`
      : `On hand (${list.total})`;

  return (
    <div className="shell">
      <header className="masthead">
        <span className="wordmark">explorer</span>
        <span className="num" style={{ color: "var(--muted)" }}>{list.total} on hand</span>
      </header>

      {error && <div className="notice">{error}</div>}

      <ScopePicker scope={scope} onChange={(s) => { setScope(s); setStore(null); }} />

      <section className="rail">
        <h2 className="eyebrow rail-label">Inventory value by store</h2>
        {byStore === null ? (
          <p className="hint">Loading…</p>
        ) : byStore.length === 0 ? (
          <p className="hint">No unsold inventory on hand.</p>
        ) : (
          <div className="breakdown">
            {byStore.map((s) => (
              <button
                key={s.store}
                type="button"
                className={`breakdown-row clickable ${store === s.store ? "active" : ""}`}
                onClick={() => setStore((cur) => (cur === s.store ? null : s.store))}
                aria-pressed={store === s.store}
              >
                <span className="breakdown-label">{s.store}</span>
                <span className="breakdown-meta">{s.units} item{s.units === 1 ? "" : "s"}</span>
                <span className="breakdown-value">{money(s.value)}</span>
              </button>
            ))}
          </div>
        )}
        <p className="hint">
          Tap a store to filter the list below. Value is what you paid (book value) for items still on hand.
        </p>
      </section>

      <InventoryList
        list={list}
        title={title}
        q={q}
        onSearch={setQ}
        filtering={filtering}
        onClear={clear}
      />
    </div>
  );
}
